import type { PMIssue, IssueStatus } from "@/domains/pm/types";
import { effectiveStartDate } from "@/domains/pm/utils/issueDates";
import { getDisciplineName } from "@/domains/pm/utils/disciplineName";

export type IssueGroupBy = "creation_date" | "due_date" | "status" | "discipline" | "assignee";

/**
 * `timeline` orders by where an issue's bar BEGINS (start_date, falling back to
 * due_date). It is deliberately absent from `IssueGroupBy`, from
 * `IssueSortControl`'s options and from every list column header: the Gantt is
 * its only consumer, and offering it as a list sort would expose a dimension
 * that view cannot show.
 */
export type IssueSortField =
  | "issue_number"
  | "due_date"
  | "status"
  | "discipline"
  | "assignee"
  | "timeline";

export interface IssueSort {
  field: IssueSortField;
  direction: "asc" | "desc";
}

/**
 * Reference back to an issue's parent that the row should render as a
 * "Subissue of #N — Title" indicator. Only populated for WHA-3331
 * orphan rendering — when the user's filter excludes the parent but
 * includes the subissue.
 */
export interface OrphanedParentRef {
  id: number;
  issue_number: number;
  title: string;
}

/**
 * One emitted row in a sorted group. Wraps the underlying PMIssue with
 * the indentation level (0 = top-level, 1 = subissue rendered directly
 * under its parent) and the optional orphan badge ref.
 */
export interface GroupedIssueItem {
  issue: PMIssue;
  indentLevel: number;
  /** Set only when the parent is NOT in the filtered set — the row
   *  renders standalone with a "Subissue of #N" chip. */
  orphanedParentRef?: OrphanedParentRef;
}

export interface IssueGrouping {
  /** Stable key for React lists and group toggle state. */
  key: string;
  /** Human-readable group label. */
  label: string;
  /** Rows belonging to this group, already sorted and tree-glued. */
  items: GroupedIssueItem[];
  /** True when the group should start collapsed. Caller can override. */
  defaultCollapsed?: boolean;
}

/** In the viewer's language: `name` is the Estonian one (see disciplineName.ts). */
function firstDisciplineName(issue: PMIssue, language: string): string {
  const d = issue.disciplines?.[0];
  return d ? getDisciplineName(d, language) : "";
}

function firstAssigneeName(issue: PMIssue): string {
  const a = issue.assignees?.[0];
  if (!a) return "";
  const full = `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim();
  return full || a.email;
}

function compareIssues(
  a: PMIssue,
  b: PMIssue,
  sort: IssueSort,
  statusOrder: IssueStatus[],
  language: string
): number {
  const dir = sort.direction === "asc" ? 1 : -1;
  switch (sort.field) {
    case "issue_number":
      return (a.issue_number - b.issue_number) * dir;
    case "due_date": {
      // Issues without a due date sort last regardless of direction.
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return (new Date(a.due_date).getTime() - new Date(b.due_date).getTime()) * dir;
    }
    case "timeline": {
      // Same null-last convention as due_date: an unscheduled issue has no
      // place on the axis, so it sorts after every scheduled one either way.
      const as = effectiveStartDate(a);
      const bs = effectiveStartDate(b);
      if (!as && !bs) return 0;
      if (!as) return 1;
      if (!bs) return -1;
      // Date-only strings compare correctly as text — no parsing, no timezone.
      return as === bs ? 0 : (as < bs ? -1 : 1) * dir;
    }
    case "status": {
      // A status missing from the order sorts LAST rather than first, which is
      // what a bare indexOf would do with its -1.
      const rank = (s: IssueStatus) => {
        const i = statusOrder.indexOf(s);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      };
      return (rank(a.status) - rank(b.status)) * dir;
    }
    case "discipline": {
      // First-discipline alphabetical. Issues with no disciplines sort last
      // regardless of direction (same null-last convention as due_date).
      const an = firstDisciplineName(a, language);
      const bn = firstDisciplineName(b, language);
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn, language) * dir;
    }
    case "assignee": {
      const an = firstAssigneeName(a);
      const bn = firstAssigneeName(b);
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn) * dir;
    }
  }
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function diffDays(a: Date, b: Date): number {
  return Math.floor((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86_400_000);
}

interface DateBucketLabels {
  today: string;
  yesterday: string;
  thisWeek: string;
  thisMonth: string;
  older: string;
  noDate: string;
  overdue: string;
  later: string;
}

/**
 * A date bucket's KEY is the label's name in `DateBucketLabels`, never the
 * label itself. Keys outlive the UI language — the Gantt's shared arrangement
 * is stored against them — so "This week" and "See nädal" must be one band.
 */
type DateBucket = keyof DateBucketLabels;

/** Pick the relative bucket for a creation-date timestamp. */
function creationDateBucket(iso: string, now: Date): DateBucket {
  const d = new Date(iso);
  const days = diffDays(now, d);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return "thisWeek";
  if (days < 31) return "thisMonth";
  return "older";
}

/** Pick the bucket for a due-date (may be null). */
function dueDateBucket(iso: string | null, now: Date): DateBucket {
  if (!iso) return "noDate";
  const d = new Date(iso);
  // Treat end-of-day as the deadline for overdue.
  const endOfDay = new Date(d);
  endOfDay.setHours(23, 59, 59, 999);
  if (endOfDay.getTime() < now.getTime()) return "overdue";
  const days = diffDays(d, now);
  if (days === 0) return "today";
  if (days < 7) return "thisWeek";
  return "later";
}

/** Bucket order is intentional — earliest/most-urgent first. */
const CREATION_DATE_ORDER: DateBucket[] = ["today", "yesterday", "thisWeek", "thisMonth", "older"];

const DUE_DATE_ORDER: DateBucket[] = ["overdue", "today", "thisWeek", "later", "noDate"];

/**
 * The list's ordering, without its grouping.
 *
 * Exported so a picker can order rows the way the issues list does — nulls
 * last whichever direction, workflow status order, first discipline/assignee
 * only — rather than growing a second comparator that drifts from this one.
 */
export function sortIssues(
  issues: PMIssue[],
  sort: IssueSort,
  language: string,
  statusOrder: IssueStatus[] = BUILT_IN_STATUS_ORDER
): PMIssue[] {
  return [...issues].sort((a, b) => compareIssues(a, b, sort, statusOrder, language));
}

/**
 * Fallback order for callers with no catalogue: the three built-ins. Real
 * callers pass `statusOrder` from `visibleStatusKeys(catalogue, projectId)`,
 * because a custom status missing from the order used to VANISH from the
 * grouping entirely (orderedKeys filtered the buckets through this list).
 */
const BUILT_IN_STATUS_ORDER: IssueStatus[] = ["in_progress", "resolved", "closed"];

export interface GroupAndSortInput {
  /** The filtered set of issues we want to render. */
  issues: PMIssue[];
  /**
   * Full unfiltered set, used only to resolve an orphan subissue's parent
   * label when the parent was filtered out (WHA-3331). Pass the same value
   * as `issues` if there is no separate filter step.
   */
  allIssues?: PMIssue[];
  groupBy: IssueGroupBy;
  sort: IssueSort;
  dateLabels: DateBucketLabels;
  statusLabels: Record<IssueStatus, string>;
  /** Ordered status keys — grouping order and the status sort. Defaults to the
   *  three built-ins; any key an issue holds that is missing from it is
   *  appended last rather than dropped. */
  statusOrder?: IssueStatus[];
  /** Label used when an issue has no disciplines and we group by discipline. */
  noDisciplineLabel: string;
  /** The interface language. Discipline groups are labelled, and ordered, by
   *  the discipline's name in it — `name` alone is always Estonian. */
  language: string;
  /** Label used when an issue has no assignees and we group by assignee. */
  noAssigneeLabel: string;
  /** Now-clock injection point (tests pass a fixed Date). */
  now?: Date;
}

/**
 * Compute the visible groups for the issues list.
 *
 * Multi-value dimensions (discipline, assignee) place the same issue in
 * every group it belongs to; this matches Linear/Jira semantics and the
 * decision recorded for WHA-3273.
 *
 * Subissues (WHA-3331) are placed into buckets via their PARENT's
 * attributes when the parent is visible — so a subissue always renders
 * in the parent's group, regardless of the subissue's own status /
 * discipline / assignee. When the parent is NOT in the filtered set,
 * the subissue falls back to its own attributes for placement and is
 * marked as orphaned so the row can render a "Subissue of #N — Title"
 * chip in lieu of indentation.
 */
export function groupAndSortIssues({
  issues,
  allIssues,
  groupBy,
  sort,
  dateLabels,
  statusLabels,
  noDisciplineLabel,
  noAssigneeLabel,
  language,
  statusOrder = BUILT_IN_STATUS_ORDER,
  now = new Date(),
}: GroupAndSortInput): IssueGrouping[] {
  const buckets = new Map<string, { label: string; items: PMIssue[] }>();

  // Lookup of issues currently visible (post-filter) by id — drives the
  // "is parent visible?" check that decides between glue-under-parent
  // vs orphan-row rendering.
  const visibleById = new Map<number, PMIssue>();
  for (const issue of issues) visibleById.set(issue.id, issue);

  // Fallback lookup for orphan parent ref hydration. We accept the full
  // unfiltered list so the row can show the parent's #N and title even
  // when the parent itself was filtered out.
  const allById = new Map<number, PMIssue>();
  for (const issue of allIssues ?? issues) allById.set(issue.id, issue);

  const push = (key: string, label: string, issue: PMIssue) => {
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { label, items: [] };
      buckets.set(key, bucket);
    }
    bucket.items.push(issue);
  };

  // For each issue, decide the placement source: the issue itself for
  // top-level rows and orphan subissues, the parent for subissues whose
  // parent is also visible (so the child inherits the parent's buckets).
  const placementSource = (issue: PMIssue): PMIssue => {
    if (issue.parent_issue_id == null) return issue;
    const parent = visibleById.get(issue.parent_issue_id);
    return parent ?? issue;
  };

  switch (groupBy) {
    case "creation_date":
      for (const issue of issues) {
        const src = placementSource(issue);
        const bucket = creationDateBucket(src.created_at, now);
        push(bucket, dateLabels[bucket], issue);
      }
      break;
    case "due_date":
      for (const issue of issues) {
        const src = placementSource(issue);
        const bucket = dueDateBucket(src.due_date, now);
        push(bucket, dateLabels[bucket], issue);
      }
      break;
    case "status":
      for (const issue of issues) {
        const src = placementSource(issue);
        push(src.status, statusLabels[src.status], issue);
      }
      break;
    case "discipline":
      for (const issue of issues) {
        const src = placementSource(issue);
        const disciplines = src.disciplines ?? [];
        if (disciplines.length === 0) {
          push("__none__", noDisciplineLabel, issue);
        } else {
          for (const d of disciplines) push(`d-${d.id}`, getDisciplineName(d, language), issue);
        }
      }
      break;
    case "assignee":
      for (const issue of issues) {
        const src = placementSource(issue);
        const assignees = src.assignees ?? [];
        if (assignees.length === 0) {
          push("__none__", noAssigneeLabel, issue);
        } else {
          for (const u of assignees) {
            const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email;
            push(`u-${u.id}`, name, issue);
          }
        }
      }
      break;
  }

  // Order groups
  let orderedKeys: string[];
  if (groupBy === "creation_date") {
    orderedKeys = CREATION_DATE_ORDER.filter((k) => buckets.has(k));
  } else if (groupBy === "due_date") {
    orderedKeys = DUE_DATE_ORDER.filter((k) => buckets.has(k));
  } else if (groupBy === "status") {
    // Any status the order does not know about is APPENDED, never dropped: a
    // bucket filtered out here would take its issues off the page silently.
    const known = statusOrder.filter((k) => buckets.has(k));
    const extra = [...buckets.keys()].filter((k) => !statusOrder.includes(k)).sort();
    orderedKeys = [...known, ...extra];
  } else {
    // discipline / assignee — alphabetical by label, with "no <x>" group last.
    orderedKeys = [...buckets.keys()].sort((a, b) => {
      if (a === "__none__") return 1;
      if (b === "__none__") return -1;
      return (buckets.get(a)?.label ?? "").localeCompare(buckets.get(b)?.label ?? "", language);
    });
  }

  // Every ordered key was read off `buckets`, so the lookup always hits; the
  // flatMap only narrows the type.
  return orderedKeys.flatMap((key) => {
    const bucket = buckets.get(key);
    if (!bucket) return [];
    const sorted = [...bucket.items].sort((a, b) =>
      compareIssues(a, b, sort, statusOrder, language)
    );

    // Build a parent → children index for this specific bucket. Children
    // are only attached to their parent if BOTH are in this same bucket
    // (a subissue inherits the parent's bucket placement above so this
    // is the common case; the only way they diverge is when the parent
    // is filtered out, which we render as an orphan instead).
    const childrenByParent = new Map<number, PMIssue[]>();
    const idsInBucket = new Set(sorted.map((i) => i.id));
    for (const issue of sorted) {
      if (
        issue.parent_issue_id != null &&
        visibleById.has(issue.parent_issue_id) &&
        idsInBucket.has(issue.parent_issue_id)
      ) {
        const list = childrenByParent.get(issue.parent_issue_id) ?? [];
        list.push(issue);
        childrenByParent.set(issue.parent_issue_id, list);
      }
    }

    const items: GroupedIssueItem[] = [];
    const emitted = new Set<number>();
    for (const issue of sorted) {
      if (emitted.has(issue.id)) continue;

      const parentId = issue.parent_issue_id;
      const parentVisible = parentId != null && visibleById.has(parentId);

      if (parentId != null && parentVisible && idsInBucket.has(parentId)) {
        // Will be emitted by its parent's branch — skip for now.
        continue;
      }

      if (parentId != null && !parentVisible) {
        // Orphan: parent filtered out. Render at top level with a ref chip.
        const parentRef = allById.get(parentId);
        items.push({
          issue,
          indentLevel: 0,
          orphanedParentRef: parentRef
            ? {
                id: parentRef.id,
                issue_number: parentRef.issue_number,
                title: parentRef.title,
              }
            : { id: parentId, issue_number: 0, title: "" },
        });
        emitted.add(issue.id);
        continue;
      }

      // Top-level issue (or subissue whose parent is in the bucket but we
      // got here because the parent itself was the visible-source — which
      // means this row IS the parent). Emit + glue children.
      items.push({ issue, indentLevel: 0 });
      emitted.add(issue.id);

      const children = childrenByParent.get(issue.id);
      if (children) {
        for (const child of children) {
          items.push({ issue: child, indentLevel: 1 });
          emitted.add(child.id);
        }
      }
    }

    return {
      key,
      label: bucket.label,
      items,
      // Closed status group starts collapsed; everything else open.
      defaultCollapsed: groupBy === "status" && key === "closed",
    };
  });
}
